import moment from "moment";
import "moment/locale/es";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";
import cuentaMask from "../Components/cuentaMask";
import ADVERTENCIAS, { TIPO_LABELS } from "../Pages/Panel/ConfiguracionNomina/VerificarContabilidadNomina/advertencias";

moment.locale("es");

const textoCuenta = (cuenta) =>
  cuenta ? `${cuentaMask(cuenta.codigo)} - ${cuenta.descripcion}` : "";

const textoAdvertencias = (claves = []) => {
  if (!claves.length) return "Correcto";
  return claves.map((c) => ADVERTENCIAS[c]?.texto || c).join(", ");
};

const layoutTabla = {
  fillColor: (rowIndex, node) => {
    if (rowIndex === 0) return "#cccccc";
    return rowIndex % 2 === 0 ? "#e8e8e8" : "#ffffff";
  },
  hLineWidth: function () { return 1; },
  vLineWidth: function () { return 1; },
  hLineColor: function () { return "#adaaaa"; },
  vLineColor: function () { return "#adaaaa"; },
};

const verificacionContabilidadNominaPdf = (values) => {
  const empresa = values.empresa;
  const logoBiwo = values.logoBiwo;
  const config = values.configuracionGeneral || {};
  const conceptos = values.conceptos || [];
  const anexoEmpleados = values.anexoEmpleados || [];

  // ── Configuración general ──────────────────────────────────────────────────
  const filasConfiguracion = [
    [
      { text: "Tipo de Póliza:", style: "subheader" },
      { text: config.tipoDePolizaNombre || "Sin configurar", style: "textoChico" },
      { text: "Proyecto General:", style: "subheader" },
      { text: config.proyectoNombre || "Sin configurar", style: "textoChico" },
    ],
    [
      { text: "Póliza por empleado:", style: "subheader" },
      { text: config.polizaPorEmpleado ? "Sí" : "No", style: "textoChico" },
      { text: "Movimientos de obligaciones:", style: "subheader" },
      {
        text: config.desactivarMovimientosObligaciones ? "Desactivados" : "Activos",
        style: "textoChico",
      },
    ],
    [
      { text: "Cuenta Intercentro:", style: "subheader" },
      {
        text: config.cuentaIntercentro ? textoCuenta(config.cuentaIntercentro) : "Sin configurar",
        style: "textoChico",
        colSpan: 3,
      },
      {},
      {},
    ],
  ];

  // ── Tabla de conceptos ─────────────────────────────────────────────────────
  const encabezadoConceptos = [
    { text: "N°", style: "tituloTabla" },
    { text: "Concepto", style: "tituloTabla" },
    { text: "Tipo", style: "tituloTabla" },
    { text: "Cuenta de Costo o Gasto", style: "tituloTabla" },
    { text: "Cuenta de Pasivo", style: "tituloTabla" },
    { text: "Cta. x Empleado", style: "tituloTabla" },
    { text: "Cons.", style: "tituloTabla" },
    { text: "Ejemplo que crearía", style: "tituloTabla" },
    { text: "Empleados", style: "tituloTabla" },
    { text: "Estatus", style: "tituloTabla" },
  ];

  const filasConceptos = conceptos.map((c) => [
    {
      text: `${c.numeroConcepto != null ? c.numeroConcepto : c.numero ?? ""}`,
      style: "textoTabla",
      alignment: "center",
    },
    {
      // Un concepto agrupado no tiene contabilidad propia, así que en vez de repetir sus
      // cuentas dormidas se anota aquí en quién cae y las demás columnas van vacías.
      text:
        `${c.descripcion || ""}` +
        `${c.noSuma ? " (Informativo)" : ""}` +
        `${c.estado === 2 ? " (Deshabilitado)" : ""}` +
        `${c.conceptoContable ? `\nSe contabiliza en: ${c.conceptoContable.descripcion}` : ""}` +
        // La entrega de un préstamo no lleva columna propia: se anota aquí y su cuenta por cobrar
        // ocupa la celda de costo/gasto, que es de donde salía el cargo antes.
        `${c.esPrestamoPercepcion
          ? `\nPréstamo entregado en la nómina${
              c.conceptoCobroPrestamo ? `, lo cobra: ${c.conceptoCobroPrestamo.descripcion}` : " (sin concepto de cobro)"
            }`
          : ""}`,
      style: "textoTabla",
    },
    { text: TIPO_LABELS[c.tipo]?.label || "", style: "textoTabla" },
    {
      text: c.heredaContabilidad
        ? "(hereda)"
        : c.conceptoCobroPrestamo
        ? `${textoCuenta(c.conceptoCobroPrestamo.cuentaPorCobrar)}\n(por cobrar al empleado)`
        : textoCuenta(c.cuentaCostoGasto),
      style: "textoTabla",
    },
    { text: c.heredaContabilidad ? "(hereda)" : textoCuenta(c.cuentaPasivo), style: "textoTabla" },
    {
      text: c.heredaContabilidad ? "" : c.generarCuentaPorEmpleado ? "Sí" : "No",
      style: "textoTabla",
      alignment: "center",
    },
    {
      text: !c.heredaContabilidad && c.generarCuentaPorEmpleado ? `${c.consecutivoCuentas ?? ""}` : "",
      style: "textoTabla",
      alignment: "center",
    },
    {
      // La entrega de un préstamo no abre subcuenta propia: usa la del concepto que lo cobra, y
      // el ejemplo de esa cuenta sale en el renglón de la deducción.
      text: c.conceptoCobroPrestamo?.generarCuentaPorEmpleado
        ? `Usa la subcuenta de ${c.conceptoCobroPrestamo.descripcion}`
        : c.ejemploCuenta
        ? `${cuentaMask(c.ejemploCuenta.codigo)} - ${c.ejemploCuenta.descripcion}`
        : "",
      style: "textoTabla",
    },
    {
      text: !c.heredaContabilidad && c.generarCuentaPorEmpleado
        ? `${c.empleadosConCuenta} / ${c.empleadosAplican}`
        : "",
      style: "textoTabla",
      alignment: "center",
    },
    { text: textoAdvertencias(c.advertencias), style: "textoTabla" },
  ]);

  if (!filasConceptos.length) {
    filasConceptos.push([
      { text: "No hay conceptos de nómina registrados", style: "textoTabla", colSpan: 10 },
      {}, {}, {}, {}, {}, {}, {}, {}, {},
    ]);
  }

  // ── Anexo: cuentas por empleado ────────────────────────────────────────────
  const contenidoAnexo = [];
  if (anexoEmpleados.length) {
    contenidoAnexo.push({
      text: "Anexo: cuentas contables por empleado",
      style: "tableHeaderIzquierda",
      pageBreak: "before",
      margin: [0, 0, 0, 6],
    });

    anexoEmpleados.forEach((anexo) => {
      const filas = (anexo.empleados || []).map((e) => [
        { text: `${e.serieNumero || e.codigo || ""}`, style: "textoTabla", alignment: "center" },
        { text: e.nombreCompleto || "", style: "textoTabla" },
        { text: e.estado === 1 ? "Activo" : "Baja", style: "textoTabla", alignment: "center" },
        {
          text: e.cuenta ? textoCuenta(e.cuenta) : "Sin cuenta — se creará al timbrar",
          style: "textoTabla",
        },
      ]);

      if (!filas.length) {
        filas.push([
          { text: "Sin empleados para este concepto", style: "textoTabla", colSpan: 4 },
          {}, {}, {},
        ]);
      }

      contenidoAnexo.push({
        text: `${anexo.numero != null ? anexo.numero + " - " : ""}${anexo.descripcion || ""}`,
        style: "subheader",
        margin: [0, 8, 0, 2],
      });

      contenidoAnexo.push({
        style: "tableExample",
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ["10%", "35%", "10%", "45%"],
          body: [
            [
              { text: "Código", style: "tituloTabla" },
              { text: "Empleado", style: "tituloTabla" },
              { text: "Estatus", style: "tituloTabla" },
              { text: "Cuenta asignada", style: "tituloTabla" },
            ],
            ...filas,
          ],
        },
        layout: layoutTabla,
      });
    });
  }

  const dd = {
    footer: {
      image: "logoBiwo",
      alignment: "center",
      width: 80,
    },
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          layout: { defaultBorder: false },
          body: [
            [
              ...HeaderEmpresaCard(empresa, false, false, {
                border: [false, false, false, false],
                table: {
                  headerRows: 1,
                  widths: ["100%"],
                  body: [
                    [{ text: "Fecha de Impresión:", style: "subheader", alignment: "center" }],
                    [{ text: moment().locale("es").format("LL"), style: "textoChico", alignment: "center" }],
                  ],
                },
                layout: "noBorders",
              }),
            ],
          ],
        },
      },
      {
        text: "Configuración Contable de Nómina",
        style: "tableHeaderIzquierda",
      },
      { text: "Configuración general:", margin: [0, 6, 0, 0] },
      {
        style: "tableExample",
        table: {
          widths: ["18%", "32%", "18%", "32%"],
          body: filasConfiguracion,
        },
        layout: {
          hLineWidth: function () { return 1; },
          vLineWidth: function () { return 1; },
          hLineColor: function () { return "#adaaaa"; },
          vLineColor: function () { return "#adaaaa"; },
        },
      },
      { text: "Conceptos de nómina:", margin: [0, 6, 0, 0] },
      {
        style: "tableExample",
        table: {
          headerRows: 1,
          widths: ["4%", "15%", "7%", "15%", "15%", "6%", "4%", "15%", "6%", "13%"],
          body: [encabezadoConceptos, ...filasConceptos],
        },
        layout: layoutTabla,
      },
      ...contenidoAnexo,
    ],
    images: {
      logo: empresa.logo.base64Completa,
      logoBiwo: logoBiwo.base64Completa,
    },
    styles: {
      tableHeaderIzquierda: { bold: true, fontSize: 16, color: "black" },
      header: { fontSize: 9, bold: true },
      subheader: { fontSize: 8, bold: true },
      tableExample: { margin: [0, 5, 0, 5] },
      tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
      textoChico: { fontSize: 8 },
      tituloTabla: {
        fontSize: 8,
        bold: true,
        fillColor: "#cccccc",
        alignment: "center",
      },
      textoTabla: { fontSize: 7, italics: false },
    },
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 50],
  };

  return dd;
};

export default verificacionContabilidadNominaPdf;
